import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, ExternalLink, Wrench } from "lucide-react";
import type {
	ImageMissingDimensionsDetails,
	PageIssue,
} from "@/types/technicalAudit";
import {
	buildImgDimensionsTagPreview,
	imgTagsFrom,
	parseIntrinsicDimensions,
} from "./issueBeforeAfter";

interface ImageMissingDimensionsCardProps {
	issue: PageIssue;
}

// image_missing_dimensions: an <img> without explicit width/height (CLS risk).
// Auto-fixable content edit — Apply inserts the missing width/height attributes
// on the flagged <img> in the post content, with values from the crawl-measured
// intrinsic dimensions (backend falls back to the media library / a remote
// probe). Theme/builder-rendered images that aren't in the post content can't
// be edited this way and come back as a recommendation. Mirrors
// NoindexImportantCard (deterministic staged fix, no Generate step).
export function ImageMissingDimensionsCard({
	issue,
}: ImageMissingDimensionsCardProps) {
	const details = (issue.issue_details ||
		{}) as Partial<ImageMissingDimensionsDetails> & Record<string, unknown>;
	const missingAttributes = Array.isArray(details.missing_attributes)
		? details.missing_attributes
		: [];
	const sourcePageUrl =
		details.source_page_url || details.page_url || issue.page_url;

	// Prefer the backend's ACTUAL before/after tags when the apply/preview path
	// recorded them (proposed_fix / approved_value / issue_details enrichment);
	// otherwise derive the tag shapes from the crawl details.
	const backendTags =
		imgTagsFrom(issue.approved_value) ??
		imgTagsFrom(issue.proposed_fix) ??
		imgTagsFrom(issue.proposed_value) ??
		imgTagsFrom(details);
	const derived = buildImgDimensionsTagPreview(details);
	const beforeTag = backendTags?.before_tag ?? derived.before;
	const afterTag = backendTags?.after_tag ?? derived.after;

	const dims = parseIntrinsicDimensions(details.intrinsic_dimensions);
	const intrinsicLabel = dims
		? `${dims.width} × ${dims.height}`
		: details.intrinsic_dimensions ||
			"Unknown — measured from the image file at apply time";

	return (
		<div className="space-y-4">
			<div className="flex items-start gap-3 rounded-xl p-4 border border-coral/40 bg-coral-light">
				<div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-coral/10">
					<AlertTriangle className="w-5 h-5 text-coral" />
				</div>
				<div className="flex-1">
					<h4 className="font-semibold text-coral-dark">
						Image missing width/height
					</h4>
					{details.image_url && (
						<a
							href={details.image_url}
							target="_blank"
							rel="noopener noreferrer"
							className="inline-flex items-center gap-1 text-sm mt-1 text-coral hover:text-coral-hover break-all"
						>
							<span className="break-all">{details.image_url}</span>
							<ExternalLink className="w-3 h-3 shrink-0" />
						</a>
					)}
				</div>
			</div>

			<div className="rounded-lg border border-border bg-card text-sm">
				{missingAttributes.length > 0 && (
					<div className="grid grid-cols-3 gap-3 border-b border-border px-3 py-2">
						<dt className="text-xs uppercase tracking-wide text-muted-foreground">
							Missing
						</dt>
						<dd className="col-span-2 flex flex-wrap gap-1.5">
							{missingAttributes.map((attr) => (
								<Badge
									key={attr}
									variant="outline"
									className="border-coral/40 bg-coral-light text-coral-dark"
								>
									{attr}
								</Badge>
							))}
						</dd>
					</div>
				)}
				<div className="grid grid-cols-3 gap-3 border-b border-border px-3 py-2">
					<dt className="text-xs uppercase tracking-wide text-muted-foreground">
						Intrinsic size
					</dt>
					<dd className="col-span-2 break-words text-foreground">
						{intrinsicLabel}
					</dd>
				</div>
				{sourcePageUrl && (
					<div className="grid grid-cols-3 gap-3 px-3 py-2">
						<dt className="text-xs uppercase tracking-wide text-muted-foreground">
							Found on
						</dt>
						<dd className="col-span-2 break-all text-foreground">
							{sourcePageUrl}
						</dd>
					</div>
				)}
			</div>

			{/* What Apply changes — deterministic content edit, no Generate step. */}
			<Card className="border-teal/30 bg-teal-light/40 shadow-sm">
				<CardHeader className="pb-3">
					<CardTitle className="flex items-center gap-2 text-sm font-semibold text-teal-dark">
						<div className="flex h-8 w-8 items-center justify-center rounded-lg bg-teal/15">
							<Wrench className="w-4 h-4 text-teal-dark" />
						</div>
						What Apply changes
					</CardTitle>
				</CardHeader>
				<CardContent className="pt-0 space-y-3">
					<div className="space-y-2">
						<div className="rounded-lg border border-border bg-card p-3">
							<p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
								Before
							</p>
							<code className="block text-sm text-foreground break-all whitespace-pre-wrap">
								{beforeTag}
							</code>
						</div>
						<div className="rounded-lg border border-teal/30 bg-card p-3">
							<p className="text-xs uppercase tracking-wide text-teal-dark mb-1">
								After
							</p>
							<code className="block text-sm font-medium text-foreground break-all whitespace-pre-wrap">
								{afterTag}
							</code>
						</div>
					</div>
					<p className="text-sm text-teal-dark">
						One click adds explicit <code>width</code>/<code>height</code>{" "}
						attributes to this image in the page content
						{derived.hasDimensions
							? " (values measured from the actual image file by the crawl)"
							: " (values resolved from the image file at apply time)"}
						, so the browser reserves space before it loads and avoids layout
						shift (CLS).
					</p>
					<p className="text-xs text-muted-foreground">
						If the image is rendered by the theme or a page builder (not found
						in the post content), it can&apos;t be edited here — the fix is
						saved as a recommendation for a template-level change instead.
					</p>
				</CardContent>
			</Card>
		</div>
	);
}
